import React, { useState, useEffect }  from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getVendors, addVendor, editVendor, removeVendor } from "./../store/features/vendors/vendorSlice";

import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, IconButton, TextField, Button, Typography,
  Box, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  TableSortLabel
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import SearchBar from '../components/common/SearchBar'

const Vendor = () => {
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const dispatch = useDispatch();
  const vendors = useSelector((state) => state.vendors.vendors);

  const [editingId, setEditingId] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [kitchenLocations, setKitchenLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState("");

  // State for filtered vendors
  const [filteredVendors, setFilteredVendors] = useState([]);
const headerMap = {
  "Vendor ID": "vendorId",
  "Establishment": "name",
  "Contact Person": "vena",
  "Designation": "designation",
  "Phone": "phn",
  "Email": "email",
  "KITCHEN": "loc",
  "FSSAI No.": "fssaiId"
};

  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    dispatch(getVendors());
  }, [dispatch]);

  // Update filteredVendors when vendors change
  useEffect(() => {
    setFilteredVendors(vendors);
  }, [vendors]);

  const onSubmit = (data) => {
    const payload = {
      name: data.establishmentName,
      phn: data.phone,
      vena: data.personName,
      loc: data.kitchenLocations,
      email: data.email,
      fssaiId: data.fssaiNumber,
      designation: data.designation
    };

    if (editingId) {
      console.log(editingId)
      dispatch(editVendor({ id: editingId, data: {id: editingId,...payload} }));
    } else {
      dispatch(addVendor(payload));
    }

    reset();
    setEditingId(null);
    setEditingIndex(null);
    setOpenDialog(false);
  };

  const handleEdit = (id) => {
    const vendor = vendors.find((v) => v._id === id);
    setValue("establishmentName", vendor.name || "");
    setValue("phone", vendor.phn || "");
    setValue("personName", vendor.vena || "");
    setValue("kitchenLocations", vendor.loc || "");
    setValue("email", vendor.email || "");
    setValue("fssaiNumber", vendor.fssaiId || "");
    setValue("designation", vendor.designation || "")
    setEditingId(vendor._id);
    console.log(vendor, "vendor._id")
    setEditingIndex(vendor._id);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
   
    dispatch(removeVendor(id));
  };

  const handleCloseDialog = () => {
    reset();
    setKitchenLocations([]);
    setEditingId(null);
    setEditingIndex(null);
    setOpenDialog(false);
  };

  const handleAddLocation = () => {
    if (currentLocation && !kitchenLocations.includes(currentLocation)) {
      setKitchenLocations([...kitchenLocations, currentLocation]);
      setCurrentLocation('');
    }
  };

  const handleRemoveLocation = (locationToRemove) => {
    setKitchenLocations(kitchenLocations.filter(loc => loc !== locationToRemove));
  };

  // Handle search
  const handleSearch = (results) => {
    setFilteredVendors(results);
    setPage(0); // Reset to the first page after search
  };

  // Sorting functions
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  return (
    <Stack spacing={4}>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#212529',
            color: '#fff',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #343a40', fontWeight: 'bold' }}>
          {editingId ? "Edit Vendor" : "Create Vendor"}
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
            <Stack spacing={3}>
              {/* Establishment Name */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Establishment Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("establishmentName", { required: "Establishment name is required" })}
                  error={!!errors.establishmentName}
                  helperText={errors.establishmentName?.message}
                  sx={fieldStyles}
                />
              </div>

              {/* Contact Person Name */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Contact Person Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("personName", { required: "Contact person is required" })}
                  error={!!errors.personName}
                  helperText={errors.personName?.message}
                  sx={fieldStyles}
                />
              </div>

              {/* Designation */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Designation
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("designation", { required: "Designation is required" })}
                  error={!!errors.designation}
                  helperText={errors.designation?.message}
                  sx={fieldStyles}
                />
              </div>

              {/* Phone Number */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Phone Number
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("phone", {
                    required: "Phone is required",
                    pattern: {
                      value: /^[0-9]{10,15}$/,
                      message: "Invalid phone number (10–15 digits)"
                    }
                  })}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  sx={fieldStyles}
                />
              </div>

              {/* Email */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+$/,
                      message: "Invalid email format"
                    }
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={fieldStyles}
                />
              </div>

              {/* FSSAI Number */}
              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  FSSAI License Number
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("fssaiNumber"
                  )}
                
                  sx={fieldStyles}
                />
              </div>

              <div>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#adb5bd' }}>
                  Kitchen Locations
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  {...register("kitchenLocations", { required: "Kitchen locations are required" })}
                  error={!!errors.kitchenLocations}
                  helperText={errors.kitchenLocations?.message}
                  sx={fieldStyles}
                />
              </div>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDialog}
              color="inherit"
              sx={{ color: '#adb5bd', '&:hover': { backgroundColor: 'rgba(173, 181, 189, 0.1)' } }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                textTransform: 'none',
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' },
                '&:disabled': { bgcolor: '#6c757d' }
              }}
            >
              {editingId ? "Update Vendor" : "Create Vendor"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

     
      <Paper elevation={3} sx={{
        bgcolor: '#1e1e1e',
        p: 2,
        borderRadius: 2,
        color: '#fff'
      }}>
        <Box display="flex" justifyContent="space-between"  flexWrap={"wrap"} mb={3}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Vendor List</Typography>
          <Box display="flex" gap={2} flexWrap={"wrap"} >
            <SearchBar
              data={vendors}
              searchKeys={["name", "vendorId", "phn", "email", "loc", "fssaiId", "vena", "designation"]}
              onSearch={handleSearch}
              placeholder="Search vendors..."
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
              reset();
              setEditingId(null);
              setKitchenLocations([]);
              setOpenDialog(true);
            }} sx={{
              textTransform: 'none',
              fontWeight:"bold",
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' }
            }}>Create Vendor</Button>
          </Box>
        </Box>

     {filteredVendors.length > 0 ? (
  <>
    <TableContainer
      sx={{
        backgroundColor: "transparent",
        maxHeight: 440,
        overflowX: "auto",
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#8d8787" }}>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Sno
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Vendor ID
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Establishment
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Contact Person
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Designation
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Phone
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Email
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              Kitchen
            </TableCell>
            <TableCell sx={{ fontWeight: "bolder", fontSize: "0.75rem", whiteSpace: "nowrap", p:2.5 }}>
              FSSAI No.
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: "bolder", fontSize: "0.75rem", p:2.5 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredVendors
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((vendor, index) => (
              <TableRow key={vendor._id} hover>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {page * rowsPerPage + index + 1}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.vendorId}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.name}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.vena}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.designation || "-"}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.phn}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.email}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.loc}
                </TableCell>
                <TableCell sx={{ color: "white", fontSize: "0.75rem", py: 0.5 }}>
                  {vendor.fssaiId}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, display: "flex" }}>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleEdit(vendor._id)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(vendor._id)}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Box
      className="mui-wrapper mui-isolated-container p-0"
      display="flex"
      justifyContent="flex-end"
      px={2}
      py={1}
    >
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        count={filteredVendors.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          background: "#8d8787",
          color: "white",
          ".MuiTablePagination-toolbar": {
            color: "white",
            fontSize: "0.75rem",
            overflow: {
              xs: "scroll",
              sm: "hidden",
            },
            minHeight: "40px",
          },
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
            color: "#adb5bd",
            fontSize: "0.75rem",
          },
          ".MuiTablePagination-actions": {
            color: "#90caf9",
          },
        }}
      />
    </Box>
  </>
) : (
  <Typography variant="body2" sx={{ p: 3, color: "#adb5bd" }}>
    No vendors found.
  </Typography>
)}

      </Paper>
    </Stack>
  );
};

const fieldStyles = {
  bgcolor: '#343a40',
  input: { color: '#fff' },
  borderRadius: 1,
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#6c757d" },
    "&:hover fieldset": { borderColor: "#adb5bd" },
    "&.Mui-focused fieldset": { borderColor: "#90caf9" }
  },
  "& .MuiFormHelperText-root": { color: '#f44336' }
};

export default Vendor;
